import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react"
import { EnkPar } from "../components/text/par"
import { EnkPrimaryButton } from "../components/common/buttons"
import { EnkFieldSet } from "../components/inputs/field-set"
import { EnkRadioButton } from "../components/inputs/radiobutton"
import { useEffect, useState } from "react"
import { QRCodeSVG } from "qrcode.react"
import { TEnkLoginResult, EnkIWPServerAuth as ApiAuth, TEnkMFAMethode } from "../api/api-iwp-server"
import { EnkInputField } from "../components/inputs/inputfield"
import { EnkInput } from "../components/inputs/input"

interface IEnkMfaDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  emailaddress: string
  password: string
  rememberMe: boolean
  mfaMethods: TEnkMFAMethode[]
  onLoggedIn?: (loginResult: TEnkLoginResult) => void
}

export default function EnkMfaDialog(props: IEnkMfaDialogProps) {
  const { open, setOpen, emailaddress, password, rememberMe, mfaMethods, onLoggedIn } = props
  const [closing, setClosing] = useState<boolean>(false)
  const [mfaMethod, setMfaMethod] = useState<"app" | "sms" | "email">("app")
  const [step, setStep] = useState<"method" | "scan" | "send" | "verify">("method")
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState<string>("")

  useEffect(() => {
    if (open) {
      setStep("method")
      setMfaMethod("app")
      setQrCode("")
      setMfaCode("")
    }
  }, [open])

  useEffect(() => {
    async function fetchQrCodeData() {
      const qrCode = await ApiAuth.getMfaQRCode(emailaddress, password)
      setQrCode(qrCode)
    }
    if (step === "scan") {
      // Simulate an API call to fetch QR code data
      void fetchQrCodeData()
    }
  }, [step, emailaddress, password])

  const handleDialogClose = () => {
    // Prevent closing the dialog when clicking outside of it
    if (!closing) {
      setOpen(true)
    }
  }

  const verifyMfaCode = async () => {
    try {
      const loginResult = await ApiAuth.verifyMfaCode(emailaddress, password, rememberMe, mfaMethod, mfaCode)
      if (onLoggedIn) {
        onLoggedIn(loginResult)
      }
    } catch (error: unknown) {
      console.error("Error verifying MFA code:", error)
    }
  }

  const handleNextClick = async () => {
    if (step === "method") {
      if (mfaMethod === "app") {
        setStep("scan")
      } else {
        setStep("send")
      }
      return
    }

    if (step === "scan") {
      setStep("verify")
      return
    }

    if (step === "send") {
      await ApiAuth.sendAuthCode(emailaddress, password, mfaMethod)
      setStep("verify")
      return
    }

    if (step === "verify") {
      await verifyMfaCode()
      setClosing(true)
      setOpen(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-96"
          >
            <div>
              {step === "method" && (
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    MFA instellen
                  </DialogTitle>
                  <div className="mt-2">
                    <EnkPar>Hier kunt u meervoudige authenticatie (MFA) instellen voor uw account.</EnkPar>
                  </div>
                  <EnkFieldSet
                    label="MFA methode"
                    description="Kies hieronder de methode die u wilt gebruiken voor
                    meervoudige authenticatie."
                    className="text-left col-span-full"
                  >
                    <EnkRadioButton
                      label="Google Authenticator App"
                      id="googleAuthApp"
                      group="mfa-method"
                      description="Gebruik de Google Authenticator app om een code in te vullen"
                      onClick={() => setMfaMethod("app")}
                    />
                    {mfaMethods.includes("sms") && (
                      <EnkRadioButton
                        label="SMS"
                        id="sms"
                        group="mfa-method"
                        description="Er wordt een code naar uw mobiel gestuurd via een SMS"
                        onClick={() => setMfaMethod("sms")}
                      />
                    )}
                    <EnkRadioButton
                      label="Email"
                      id="email"
                      group="mfa-method"
                      disabled={!mfaMethods.includes("email")}
                      description="Er wordt een code naar uw emailadres gestuurd"
                      onClick={() => setMfaMethod("email")}
                    />
                  </EnkFieldSet>
                </div>
              )}
              {step === "scan" && (
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    Scan QR code
                  </DialogTitle>
                  <div className="mt-2">
                    <EnkPar>Scan de QR code met de Google Authenticator app, om een code uit te rekenen.</EnkPar>
                  </div>
                  <div className="flex justify-center mt-4">
                    {qrCode && <QRCodeSVG value={qrCode || ""} size={256} />}
                    {!qrCode && <div className="w-32 h-32 bg-gray-200 animate-pulse" />}
                  </div>
                </div>
              )}

              {step === "send" && (
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    Code vesturen
                  </DialogTitle>
                  <div className="mt-2">
                    {mfaMethod === "email" && (
                      <EnkPar>
                        Klik op "Volgende" om een code naar het emailadres {emailaddress} te verzenden. Deze code heeft u nodig in het volgende
                        scherm.
                      </EnkPar>
                    )}
                    {mfaMethod === "sms" && (
                      <EnkPar>
                        Klik op "Volgende" om een code naar het telefoonnummer dat aan uw account is gekoppeld te verzenden. Deze code heeft u nodig
                        in het volgende scherm.
                      </EnkPar>
                    )}
                  </div>
                </div>
              )}

              {step === "verify" && (
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    Code verifiëren
                  </DialogTitle>
                  <div className="mt-2">
                    {mfaMethod === "app" && <EnkPar>Vul de verificatiecode (6 cijfers) uit de google authenticator app hier in.</EnkPar>}
                    {mfaMethod === "sms" && <EnkPar>Vul de verificatiecode uit de SMS hier in.</EnkPar>}
                    {mfaMethod === "email" && <EnkPar>Vul de verificatiecode uit de email hier in.</EnkPar>}
                  </div>
                  <div className="mt-4">
                    <EnkInputField label="Verificatiecode" className="text-left">
                      <EnkInput id="mfa-code" type="text" onChange={(e) => setMfaCode(e.target.value)} />
                    </EnkInputField>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 sm:mt-6">
              <EnkPrimaryButton text="Volgende" fullWidth onClick={() => void handleNextClick()} />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
